import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BugOutlined,
  CheckOutlined,
  CloudServerOutlined,
  CopyOutlined,
  DisconnectOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from '../../utils/axios';
import { showApiError } from '../../utils/apiError';
import { formatDateTime, fromNow } from '../../utils/dateFormat';
import { sourceLabel, orderStatusLabel } from '../../constants/orderDisplay';
import {
  PageHeader,
  StatCard,
  StatGrid,
  TableCard,
  Section,
  StatusTag,
  DRAWER_WIDTH,
  MODAL_WIDTH,
  tableDefaults,
  paginationDefaults,
} from '../../components/admin';

const { Text, Link } = Typography;

/** Exact time with seconds, for matching a record against server or supplier logs. */
const fmtExact = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-');

const CALLBACK_OUTCOME_LABELS = {
  unmatched_order: 'No matching order',
  missing_identifier: 'No order identifier in payload',
  unauthorized: 'Bad internal API key',
  stale_timestamp: 'Timestamp outside tolerance',
  handler_error: 'Our handler threw',
};

const API_OUTCOME = {
  partial: { label: 'Partial', tone: 'info' },
  rejected: { label: 'Rejected', tone: 'warning' },
  error: { label: 'Server error', tone: 'danger' },
};

const ORDER_STATUS_TONE = { pending: 'warning', completed: 'success', failed: 'danger' };

const WINDOW_OPTIONS = [
  { value: 60, label: 'Last hour' },
  { value: 360, label: 'Last 6 hours' },
  { value: 1440, label: 'Last 24 hours' },
];

/** How many top error codes fit on the tile before the rest move into a tooltip. */
const TOP_CODES_SHOWN = 4;

const CODE_BLOCK_STYLE = {
  margin: 0,
  overflow: 'auto',
  padding: 12,
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border-soft)',
  borderRadius: 8,
  fontSize: 12,
  lineHeight: 1.6,
  whiteSpace: 'pre',
  color: 'var(--c-text)',
};

/** Monospace, scrollable block for JSON from a supplier or a client. */
const CodeBlock = ({ value, maxHeight = 320 }) => (
  <pre className="cell-mono" style={{ ...CODE_BLOCK_STYLE, maxHeight }}>
    {value}
  </pre>
);

/** "Copy" link that copies `text` (works without the async clipboard API). */
const CopyLink = ({ text, label = 'Copy' }) => (
  <Text
    copyable={{
      text,
      tooltips: false,
      icon: [
        <span key="copy">
          <CopyOutlined /> {label}
        </span>,
        <span key="done">
          <CheckOutlined /> Copied
        </span>,
      ],
    }}
  />
);

/** Small label above a row of tiles, with an optional note and control. */
const GroupHeading = ({ title, note, extra }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      margin: '4px 0 10px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
      <h2 className="group-heading" style={{ margin: 0 }}>
        {title}
      </h2>
      {note ? <span className="cell-sub">{note}</span> : null}
    </div>
    {extra}
  </div>
);

const TimeCell = ({ value }) => (
  <div>
    <div>{formatDateTime(value)}</div>
    <div className="cell-sub">{fromNow(value)}</div>
  </div>
);

const outcomeTag = (outcome) => {
  const entry = API_OUTCOME[outcome] || {};
  return <StatusTag tone={entry.tone || 'default'}>{entry.label || outcome || '-'}</StatusTag>;
};

const errorCodeTags = (codes) =>
  codes?.length
    ? codes.map((c) => (
        <Tag key={c} bordered={false} className="cell-mono" style={{ marginInlineEnd: 4 }}>
          {c}
        </Tag>
      ))
    : '-';

/**
 * Supplier operations: the three places a member-facing incident leaves a
 * trace when it never became a normal order — health counters, supplier
 * callbacks that reached us but matched nothing, and external API requests
 * that were refused or crashed. All three backends already existed; this
 * page is the first UI on top of them.
 */
const SupplierOps = () => {
  const navigate = useNavigate();

  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(60);

  const [activeTab, setActiveTab] = useState('callbacks');

  const [callbacks, setCallbacks] = useState([]);
  const [callbacksTotal, setCallbacksTotal] = useState(0);
  const [callbacksPage, setCallbacksPage] = useState(1);
  const [callbacksResolved, setCallbacksResolved] = useState('false');
  const [callbacksLoading, setCallbacksLoading] = useState(false);
  const [payloadModal, setPayloadModal] = useState(null);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsTotal, setApiLogsTotal] = useState(0);
  const [apiLogsPage, setApiLogsPage] = useState(1);
  const [apiLogsFilter, setApiLogsFilter] = useState({ outcome: '', search: '' });
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [apiLogDetail, setApiLogDetail] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await axios.get('/admin/external-orders/health', { params: { windowMinutes } });
      setHealth(data?.data || null);
    } catch (error) {
      showApiError(error, 'Failed to load supplier health');
    } finally {
      setHealthLoading(false);
    }
  }, [windowMinutes]);

  const fetchCallbacks = useCallback(async (page = 1) => {
    setCallbacksLoading(true);
    try {
      const params = { page, limit: 20 };
      if (callbacksResolved !== 'all') params.resolved = callbacksResolved;
      const { data } = await axios.get('/admin/provider-callback-logs', { params });
      setCallbacks(Array.isArray(data?.data) ? data.data : []);
      setCallbacksTotal(data?.pagination?.totalCount || 0);
      setCallbacksPage(page);
    } catch (error) {
      showApiError(error, 'Failed to load dropped callbacks');
    } finally {
      setCallbacksLoading(false);
    }
  }, [callbacksResolved]);

  const fetchApiLogs = useCallback(async (page = 1) => {
    setApiLogsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (apiLogsFilter.outcome) params.outcome = apiLogsFilter.outcome;
      const search = apiLogsFilter.search.trim();
      if (search) {
        // The backend indexes these separately; guess the most likely field
        // from the shape and let the operator pick another via the dropdown.
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(search)) params.requestId = search;
        else if (/^ORD-/i.test(search)) params.orderNumber = search;
        else if (/^[a-z]+_/i.test(search)) params.voucherId = search;
        else params.referenceId = search;
      }
      const { data } = await axios.get('/admin/external-api-logs', { params });
      setApiLogs(Array.isArray(data?.data) ? data.data : []);
      setApiLogsTotal(data?.pagination?.totalCount || 0);
      setApiLogsPage(page);
    } catch (error) {
      showApiError(error, 'Failed to load external API logs');
    } finally {
      setApiLogsLoading(false);
    }
  }, [apiLogsFilter]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);
  useEffect(() => { fetchCallbacks(1); }, [fetchCallbacks]);
  useEffect(() => {
    const t = setTimeout(() => fetchApiLogs(1), apiLogsFilter.search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchApiLogs, apiLogsFilter.search]);

  const openOrders = (state) => navigate('/admin/orders', { state });

  const showDroppedCallbacks = () => {
    setActiveTab('callbacks');
    window.requestAnimationFrame(() => {
      document.getElementById('dropped-callbacks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const revealPayload = (log) => {
    Modal.confirm({
      title: 'Reveal raw supplier payload?',
      content:
        'The payload holds the card numbers and secrets the supplier delivered. Revealing it is logged with your admin id. Only do this to compensate the member.',
      okText: 'Reveal payload',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const { data } = await axios.get(`/admin/provider-callback-logs/${log._id}`, { params: { includePayload: 'true' } });
          setPayloadModal(data?.data || null);
        } catch (error) {
          showApiError(error, 'Failed to load payload');
        }
      },
    });
  };

  const submitResolve = async () => {
    if (!resolveModal) return;
    setResolving(true);
    try {
      await axios.patch(`/admin/provider-callback-logs/${resolveModal._id}/resolve`, { note: resolveNote || undefined });
      message.success('Marked resolved');
      setResolveModal(null);
      setResolveNote('');
      await Promise.all([fetchCallbacks(callbacksPage), fetchHealth()]);
    } catch (error) {
      showApiError(error, 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const openApiLogDetail = async (requestId) => {
    setDetailLoadingId(requestId);
    try {
      const { data } = await axios.get(`/admin/external-api-logs/${encodeURIComponent(requestId)}`);
      setApiLogDetail(data?.data || null);
    } catch (error) {
      showApiError(error, 'Failed to load request detail');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const needs = health?.needsAttention || {};
  const healthy = health?.healthy === true;
  const tilesLoading = healthLoading && !health;
  const windowLabel = (WINDOW_OPTIONS.find((o) => o.value === windowMinutes)?.label || `Last ${windowMinutes} min`).toLowerCase();
  const topCodes = health?.externalApi?.topErrorCodes || [];

  const callbackColumns = [
    {
      title: 'Received',
      dataIndex: 'createdAt',
      width: 150,
      render: (v) => <TimeCell value={v} />,
    },
    {
      title: 'Supplier',
      dataIndex: 'provider',
      width: 120,
      render: (p) => <span className="cell-main">{sourceLabel(p)}</span>,
    },
    {
      title: 'Why it was dropped',
      dataIndex: 'outcome',
      render: (o, row) => (
        <div>
          <StatusTag tone={o === 'unmatched_order' ? 'warning' : 'danger'}>{CALLBACK_OUTCOME_LABELS[o] || o}</StatusTag>
          {row.error ? (
            <div className="cell-sub" style={{ color: 'var(--c-danger)', maxWidth: 320, whiteSpace: 'normal', marginTop: 2 }}>
              {row.error}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Our order no.',
      dataIndex: 'customerOrderNo',
      render: (v) =>
        v ? (
          <Space size={4}>
            <Tooltip title="Find this order on the Orders page">
              <Link className="cell-mono" onClick={() => openOrders({ search: v })}>
                {v}
              </Link>
            </Tooltip>
            <Text copyable={{ text: v }} />
          </Space>
        ) : (
          <span className="muted">-</span>
        ),
    },
    {
      title: 'Supplier order no.',
      dataIndex: 'orderNo',
      render: (v) => (v ? <Text className="cell-mono" copyable>{v}</Text> : <span className="muted">-</span>),
    },
    {
      title: 'Supplier status',
      dataIndex: 'status',
      width: 130,
      render: (s, row) => (
        <div>
          <div>{s || '-'}</div>
          {row.cardsCount ? <div className="cell-sub">{row.cardsCount} card(s) inside</div> : null}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'resolved',
      width: 200,
      render: (_, row) =>
        row.resolved ? (
          <div>
            <StatusTag tone="success">Resolved</StatusTag>
            <div className="cell-sub" style={{ marginTop: 2 }}>
              {row.resolvedBy} · {formatDateTime(row.resolvedAt)}
            </div>
            {row.resolutionNote ? (
              <div className="cell-sub" style={{ maxWidth: 260, whiteSpace: 'normal' }}>
                {row.resolutionNote}
              </div>
            ) : null}
          </div>
        ) : (
          <StatusTag tone="danger">Open</StatusTag>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <div className="table-actions">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => revealPayload(row)}>
            Payload
          </Button>
          {!row.resolved ? (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setResolveModal(row);
                setResolveNote('');
              }}
            >
              Resolve
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const apiLogColumns = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      width: 150,
      render: (v) => <TimeCell value={v} />,
    },
    {
      title: 'Request',
      key: 'req',
      render: (_, row) => (
        <div>
          <div className="cell-mono cell-main">
            {row.method} {row.path}
          </div>
          <div className="cell-sub">
            {row.clientName || 'Unauthenticated'}
            {row.durationMs != null ? ` · ${row.durationMs} ms` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Result',
      key: 'result',
      width: 160,
      render: (_, row) => (
        <Space size={6}>
          {outcomeTag(row.outcome)}
          <span className="cell-mono muted">{row.statusCode}</span>
        </Space>
      ),
    },
    { title: 'Error codes', dataIndex: 'errorCodes', render: errorCodeTags },
    {
      title: 'Suppliers',
      dataIndex: 'providers',
      width: 130,
      render: (p) => (p?.length ? p.map((x) => sourceLabel(x)).join(', ') : '-'),
    },
    {
      title: 'VIO references',
      dataIndex: 'referenceIds',
      render: (refs) =>
        refs?.length
          ? refs.map((r) => (
              <div key={r}>
                <Text className="cell-mono" copyable style={{ fontSize: 12 }}>
                  {r}
                </Text>
              </div>
            ))
          : '-',
    },
    {
      title: 'Request id',
      dataIndex: 'requestId',
      width: 140,
      render: (id) => (
        <Text className="cell-mono" copyable={{ text: String(id) }} style={{ fontSize: 12 }}>
          {String(id).slice(0, 8)}…
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          loading={detailLoadingId === row.requestId}
          onClick={(e) => {
            e.stopPropagation();
            openApiLogDetail(row.requestId);
          }}
        >
          Detail
        </Button>
      ),
    },
  ];

  const relatedOrderColumns = [
    {
      title: 'Order',
      dataIndex: 'orderNumber',
      render: (n, row) => (
        <Link
          className="cell-mono"
          onClick={() => {
            setApiLogDetail(null);
            openOrders({ orderId: row._id });
          }}
        >
          {n}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s) => <StatusTag tone={ORDER_STATUS_TONE[s] || 'default'}>{orderStatusLabel(s)}</StatusTag>,
    },
    { title: 'Stage', key: 'stage', render: (_, row) => row.externalOrderData?.purchaseStage || '-' },
    { title: 'Failure reason', dataIndex: 'failureReason', render: (r) => r || '-' },
    { title: 'Created', dataIndex: 'createdAt', render: formatDateTime },
  ];

  const payloadJson = payloadModal ? JSON.stringify(payloadModal.payload ?? {}, null, 2) : '';
  const requestBodyJson = apiLogDetail ? JSON.stringify(apiLogDetail.requestBody ?? null, null, 2) : '';
  const responseBodyJson = apiLogDetail ? JSON.stringify(apiLogDetail.responseBody ?? null, null, 2) : '';
  const resolveOrderRef = resolveModal?.customerOrderNo || resolveModal?.orderNo;

  return (
    <div>
      <PageHeader
        title="Supplier ops"
        subtitle="Supplier orders, callbacks and API requests that need a person. Zero across the board means no member is waiting on us."
        tags={
          health ? (
            <StatusTag tone={healthy ? 'success' : 'warning'}>{healthy ? 'All clear' : 'Needs a person'}</StatusTag>
          ) : null
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={healthLoading}
            onClick={() => {
              fetchHealth();
              fetchCallbacks(callbacksPage);
              fetchApiLogs(apiLogsPage);
            }}
          >
            Refresh
          </Button>
        }
      />

      <GroupHeading
        title="Needs action"
        note={healthy ? 'Nothing is waiting on a person right now.' : 'Click a tile to open the matching orders.'}
      />
      <StatGrid min={220}>
        <StatCard
          label="Supplier outcome unknown"
          value={needs.uncertainOrders ?? '-'}
          icon={<QuestionCircleOutlined />}
          tone={needs.uncertainOrders > 0 ? 'warning' : 'default'}
          highlight={needs.uncertainOrders > 0}
          hint="Verify with the supplier, then reconcile"
          loading={tilesLoading}
          onClick={() => openOrders({ attention: 'uncertain' })}
        />
        <StatCard
          label="Refund failed"
          value={needs.refundFailedOrders ?? '-'}
          icon={<RollbackOutlined />}
          tone={needs.refundFailedOrders > 0 ? 'danger' : 'default'}
          highlight={needs.refundFailedOrders > 0}
          hint="Client charged, not refunded. Retry it"
          loading={tilesLoading}
          onClick={() => openOrders({ attention: 'refund_failed' })}
        />
        <StatCard
          label="Dropped callbacks"
          value={needs.unresolvedDroppedCallbacks ?? '-'}
          icon={<DisconnectOutlined />}
          tone={needs.unresolvedDroppedCallbacks > 0 ? 'danger' : 'default'}
          highlight={needs.unresolvedDroppedCallbacks > 0}
          hint="A member may be owed codes"
          loading={tilesLoading}
          onClick={showDroppedCallbacks}
        />
        <StatCard
          label="Zhongmin pending, no callback"
          value={needs.zhongminPendingWithoutCallback ?? '-'}
          icon={<HourglassOutlined />}
          tone={needs.zhongminPendingWithoutCallback > 0 ? 'warning' : 'default'}
          highlight={needs.zhongminPendingWithoutCallback > 0}
          hint="Over 30 min. Check ZHONGMIN_CALLBACK_URL"
          loading={tilesLoading}
          onClick={() => openOrders({ attention: 'stuck_pending', source: 'zhongmin' })}
        />
      </StatGrid>

      <GroupHeading
        title="Health"
        note="API counts cover the selected window."
        extra={
          <Select
            size="small"
            value={windowMinutes}
            onChange={setWindowMinutes}
            options={WINDOW_OPTIONS}
            style={{ width: 140 }}
            aria-label="API window"
          />
        }
      />
      <StatGrid min={220}>
        <StatCard
          label="Pending over 30 min"
          value={health?.stuckPendingOrders ?? '-'}
          icon={<FieldTimeOutlined />}
          hint="Supplier orders still waiting on a result"
          loading={tilesLoading}
          onClick={() => openOrders({ attention: 'stuck_pending' })}
        />
        <StatCard
          label="API server errors (5xx)"
          value={health?.externalApi?.serverErrors ?? '-'}
          icon={<CloudServerOutlined />}
          tone={health?.externalApi?.serverErrors > 0 ? 'danger' : 'default'}
          highlight={health?.externalApi?.serverErrors > 0}
          hint={`In the ${windowLabel}`}
          loading={tilesLoading}
        />
        <StatCard
          label="API rejections (4xx)"
          value={health?.externalApi?.rejections ?? '-'}
          icon={<StopOutlined />}
          hint={`In the ${windowLabel}`}
          loading={tilesLoading}
        />
        <div className="stat-card">
          <div className="stat-card__top">
            <span className="stat-card__label">Top error codes</span>
            <span className="stat-card__icon">
              <BugOutlined />
            </span>
          </div>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {!health ? (
              <span className="muted">-</span>
            ) : topCodes.length === 0 ? (
              <span className="cell-sub">None in the {windowLabel}</span>
            ) : (
              <>
                {topCodes.slice(0, TOP_CODES_SHOWN).map((e) => (
                  <Tag key={e.code} bordered={false} className="cell-mono" style={{ marginInlineEnd: 0 }}>
                    {e.code} × {e.count}
                  </Tag>
                ))}
                {topCodes.length > TOP_CODES_SHOWN ? (
                  <Tooltip
                    title={topCodes
                      .slice(TOP_CODES_SHOWN)
                      .map((e) => `${e.code} × ${e.count}`)
                      .join(', ')}
                  >
                    <Tag bordered={false} style={{ marginInlineEnd: 0 }}>
                      +{topCodes.length - TOP_CODES_SHOWN} more
                    </Tag>
                  </Tooltip>
                ) : null}
              </>
            )}
          </div>
        </div>
      </StatGrid>

      <div id="dropped-callbacks" style={{ scrollMarginTop: 72 }}>
        <TableCard>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarStyle={{ margin: 0, padding: '0 16px' }}
            items={[
              {
                key: 'callbacks',
                label: (
                  <Space size={8}>
                    Dropped callbacks
                    {needs.unresolvedDroppedCallbacks > 0 ? (
                      <StatusTag tone="danger" dot={false}>
                        {needs.unresolvedDroppedCallbacks} open
                      </StatusTag>
                    ) : null}
                  </Space>
                ),
                children: (
                  <>
                    <div className="table-card__toolbar">
                      <Space wrap size={[12, 8]}>
                        <Select
                          value={callbacksResolved}
                          onChange={setCallbacksResolved}
                          style={{ width: 160 }}
                          aria-label="Resolution"
                          options={[
                            { value: 'false', label: 'Open only' },
                            { value: 'true', label: 'Resolved only' },
                            { value: 'all', label: 'All callbacks' },
                          ]}
                        />
                        <span className="cell-sub">
                          Callbacks that reached Vouchain but could not be applied to an order. Kept for about 180 days.
                        </span>
                      </Space>
                    </div>
                    <Table
                      {...tableDefaults}
                      rowKey="_id"
                      loading={callbacksLoading}
                      dataSource={callbacks}
                      columns={callbackColumns}
                      locale={{
                        emptyText: (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                              callbacksResolved === 'false'
                                ? 'No open dropped callbacks. Every supplier fulfillment reached its order.'
                                : 'No callbacks match this filter'
                            }
                          />
                        ),
                      }}
                      pagination={{
                        current: callbacksPage,
                        pageSize: 20,
                        total: callbacksTotal,
                        onChange: (p) => fetchCallbacks(p),
                        showSizeChanger: false,
                        showTotal: paginationDefaults.showTotal,
                      }}
                    />
                  </>
                ),
              },
              {
                key: 'api',
                label: 'Failed API requests',
                children: (
                  <>
                    <div className="table-card__toolbar">
                      <Space wrap size={[12, 8]}>
                        <Input
                          allowClear
                          prefix={<SearchOutlined />}
                          placeholder="VIO reference, request id, ORD-…, or voucher id"
                          style={{ width: 340, maxWidth: '100%' }}
                          value={apiLogsFilter.search}
                          onChange={(e) => setApiLogsFilter((f) => ({ ...f, search: e.target.value }))}
                        />
                        <Select
                          allowClear
                          placeholder="All outcomes"
                          style={{ width: 170 }}
                          value={apiLogsFilter.outcome || undefined}
                          onChange={(v) => setApiLogsFilter((f) => ({ ...f, outcome: v || '' }))}
                          options={[
                            { value: 'rejected', label: 'Rejected (4xx)' },
                            { value: 'partial', label: 'Partial (207)' },
                            { value: 'error', label: 'Server error (5xx)' },
                          ]}
                        />
                        <span className="cell-sub">
                          Only failed requests are kept; successful ones are described by their order. Kept for about 90 days.
                        </span>
                      </Space>
                    </div>
                    <Table
                      {...tableDefaults}
                      rowKey="requestId"
                      loading={apiLogsLoading}
                      dataSource={apiLogs}
                      columns={apiLogColumns}
                      rowClassName={() => 'row-clickable'}
                      onRow={(row) => ({ onClick: () => openApiLogDetail(row.requestId) })}
                      locale={{
                        emptyText: (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No failed requests match these filters" />
                        ),
                      }}
                      pagination={{
                        current: apiLogsPage,
                        pageSize: 20,
                        total: apiLogsTotal,
                        onChange: (p) => fetchApiLogs(p),
                        showSizeChanger: false,
                        showTotal: paginationDefaults.showTotal,
                      }}
                    />
                  </>
                ),
              },
            ]}
          />
        </TableCard>
      </div>

      <Modal
        title="Raw supplier payload"
        open={!!payloadModal}
        onCancel={() => setPayloadModal(null)}
        footer={<Button onClick={() => setPayloadModal(null)}>Close</Button>}
        width={MODAL_WIDTH.lg}
        destroyOnHidden
      >
        {payloadModal && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Contains live card numbers and secrets. Do not paste it anywhere public."
            />
            <Section title="Callback">
              <dl className="kv">
                <dt>Supplier</dt>
                <dd>{sourceLabel(payloadModal.provider)}</dd>
                <dt>Received</dt>
                <dd>{fmtExact(payloadModal.createdAt)}</dd>
                <dt>Our order no.</dt>
                <dd>
                  {payloadModal.customerOrderNo ? (
                    <Text className="cell-mono" copyable>
                      {payloadModal.customerOrderNo}
                    </Text>
                  ) : (
                    '-'
                  )}
                </dd>
                <dt>Supplier order no.</dt>
                <dd>
                  {payloadModal.orderNo ? (
                    <Text className="cell-mono" copyable>
                      {payloadModal.orderNo}
                    </Text>
                  ) : (
                    '-'
                  )}
                </dd>
              </dl>
            </Section>
            <Section title="Payload" extra={<CopyLink text={payloadJson} label="Copy JSON" />}>
              <CodeBlock value={payloadJson} maxHeight={420} />
            </Section>
          </>
        )}
      </Modal>

      <Modal
        title="Resolve dropped callback"
        open={!!resolveModal}
        onCancel={() => setResolveModal(null)}
        width={MODAL_WIDTH.sm}
        destroyOnHidden
        maskClosable={!resolving}
        footer={[
          <Button key="cancel" onClick={() => setResolveModal(null)} disabled={resolving}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={resolving} onClick={submitResolve}>
            Mark resolved
          </Button>,
        ]}
      >
        {resolveModal ? (
          <>
            <p style={{ marginTop: 0 }}>
              The dropped <strong>{sourceLabel(resolveModal.provider)}</strong> callback
              {resolveOrderRef ? (
                <>
                  {' '}for <strong className="cell-mono">{resolveOrderRef}</strong>
                </>
              ) : null}{' '}
              will be closed and stop counting as needing action. Nothing is sent to the member or the supplier,
              and it cannot be reopened here.
            </p>
            <p className="muted">
              Only resolve after the member has been compensated (codes delivered or refund issued).
            </p>
            <Form layout="vertical" requiredMark={false}>
              <Form.Item label="What was done" style={{ marginBottom: 0 }}>
                <Input.TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  autoFocus
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder="e.g. Codes forwarded to VIO support ticket #1234 on 2026-09-22"
                />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Modal>

      <Drawer
        title={
          apiLogDetail ? (
            <Space size={10}>
              <span>
                Request <span className="cell-mono">{String(apiLogDetail.requestId).slice(0, 8)}</span>
              </span>
              {outcomeTag(apiLogDetail.outcome)}
            </Space>
          ) : (
            'Request'
          )
        }
        open={!!apiLogDetail}
        onClose={() => setApiLogDetail(null)}
        width={DRAWER_WIDTH.lg}
        destroyOnHidden
      >
        {apiLogDetail && (
          <>
            <Section title="Request">
              <dl className="kv">
                <dt>Request id</dt>
                <dd>
                  <Text className="cell-mono" copyable>
                    {apiLogDetail.requestId}
                  </Text>
                </dd>
                <dt>Time</dt>
                <dd>
                  {fmtExact(apiLogDetail.createdAt)} <span className="muted">({fromNow(apiLogDetail.createdAt)})</span>
                </dd>
                <dt>Client</dt>
                <dd>{apiLogDetail.clientName || 'Unauthenticated'}</dd>
                <dt>Endpoint</dt>
                <dd className="cell-mono">
                  {apiLogDetail.method} {apiLogDetail.path}
                </dd>
                <dt>Result</dt>
                <dd>
                  <Space size={6}>
                    {outcomeTag(apiLogDetail.outcome)}
                    <span className="cell-mono muted">{apiLogDetail.statusCode}</span>
                  </Space>
                </dd>
                <dt>Duration</dt>
                <dd>
                  {apiLogDetail.durationMs != null ? `${apiLogDetail.durationMs} ms` : '-'}
                  {apiLogDetail.queueWaitMs ? ` (queued ${apiLogDetail.queueWaitMs} ms)` : ''}
                </dd>
                <dt>Error codes</dt>
                <dd>{errorCodeTags(apiLogDetail.errorCodes)}</dd>
                {apiLogDetail.providers?.length ? (
                  <>
                    <dt>Suppliers</dt>
                    <dd>{apiLogDetail.providers.map((x) => sourceLabel(x)).join(', ')}</dd>
                  </>
                ) : null}
                <dt>VIO references</dt>
                <dd>
                  {apiLogDetail.referenceIds?.length
                    ? apiLogDetail.referenceIds.map((r) => (
                        <div key={r}>
                          <Text className="cell-mono" copyable>
                            {r}
                          </Text>
                        </div>
                      ))
                    : '-'}
                </dd>
              </dl>
            </Section>

            {apiLogDetail.relatedOrders?.length > 0 && (
              <Section title="Orders this request touched" description="Opening an order leaves this page.">
                <Table
                  size="small"
                  rowKey="_id"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  dataSource={apiLogDetail.relatedOrders}
                  columns={relatedOrderColumns}
                />
              </Section>
            )}

            <Section
              title="Request body"
              description="Secrets are redacted before the request is stored."
              extra={<CopyLink text={requestBodyJson} />}
            >
              <CodeBlock value={requestBodyJson} />
            </Section>

            <Section title="Response body" extra={<CopyLink text={responseBodyJson} />}>
              <CodeBlock value={responseBodyJson} />
            </Section>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default SupplierOps;
